import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Receipt, Send, CheckCircle2, AlertCircle, XCircle, Eye, Loader2, ExternalLink, LayoutGrid, List as ListIcon, FileText } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { k: "pendiente_revision", label: "🔵 Pendiente revisión", color: "border-blue-700/40 text-blue-300" },
  { k: "enviable", label: "✅ Enviable", color: "border-emerald-700/40 text-emerald-300" },
  { k: "no_valida", label: "❌ No válida", color: "border-zinc-700 text-zinc-400" },
  { k: "enviada", label: "📤 Enviadas", color: "border-amber-700/40 text-amber-300" },
];

export default function GestoriaInmatic() {
  const [tab, setTab] = useState("pendiente_revision");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selection, setSelection] = useState({}); // {tipo:id -> true}
  const [resendStatus, setResendStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [motivoModal, setMotivoModal] = useState(null);
  const [motivoText, setMotivoText] = useState("");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("inmatic_view") || "galeria");

  useEffect(() => { localStorage.setItem("inmatic_view", viewMode); }, [viewMode]);

  const load = async () => {
    setLoading(true); setSelection({});
    try {
      const { data } = await api.get("/gestoria/inmatic/pendientes", { params: { estado: tab } });
      setItems(data || []);
    } catch (err) {
      toast.error("Error cargando documentos");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]);
  useEffect(() => { api.get("/integrations/resend/status").then((r) => setResendStatus(r.data)); }, []);

  const keyOf = (it) => `${it._tipo}:${it.id}`;
  const toggle = (it) => setSelection((s) => ({ ...s, [keyOf(it)]: !s[keyOf(it)] }));
  const toggleAll = () => {
    if (Object.values(selection).filter(Boolean).length === items.length) setSelection({});
    else setSelection(Object.fromEntries(items.map((it) => [keyOf(it), true])));
  };
  const selectedItems = items.filter((it) => selection[keyOf(it)]);

  const marcar = async (estado, motivo = null) => {
    if (selectedItems.length === 0) return toast.error("Selecciona al menos un documento");
    try {
      await api.post("/gestoria/inmatic/marcar", {
        items: selectedItems.map((it) => ({ tipo: it._tipo, id: it.id, estado, motivo })),
      });
      toast.success(`✅ ${selectedItems.length} marcado(s) como ${estado.replace("_", " ")}`);
      load();
    } catch (err) {
      toast.error("Error marcando");
    }
  };

  const marcarNoValida = () => {
    if (selectedItems.length === 0) return toast.error("Selecciona al menos un documento");
    setMotivoModal({ singleItem: null });
    setMotivoText("");
  };
  const confirmarNoValida = async () => {
    const motivo = motivoText.trim() || null;
    // Caso single (galería)
    if (motivoModal?.singleItem) {
      await marcarIndividual(motivoModal.singleItem, "no_valida", motivo);
    } else {
      await marcar("no_valida", motivo);
    }
    setMotivoModal(null);
  };

  const enviarInmatic = async () => {
    const items_send = selectedItems.filter((it) => it.estado_inmatic === "enviable");
    if (items_send.length === 0) return toast.error("Solo documentos en estado 'enviable' se envían");
    if (!resendStatus?.configured) return toast.error("Resend no configurado. Pídele al admin la API key.");
    setSending(true);
    try {
      const { data } = await api.post("/gestoria/inmatic/enviar", {
        items: items_send.map((it) => ({ tipo: it._tipo, id: it.id })),
      });
      toast.success(`📤 ${data.enviados} enviado(s) a ${data.destinatario} · ${data.total_importe?.toFixed(2)} €`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al enviar");
    } finally { setSending(false); }
  };

  const marcarIndividual = async (it, estado, motivo = null) => {
    try {
      await api.post("/gestoria/inmatic/marcar", {
        items: [{ tipo: it._tipo, id: it.id, estado, motivo }],
      });
      // Optimistic: quitarlo del listado inmediatamente
      setItems((arr) => arr.filter((x) => keyOf(x) !== keyOf(it)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al marcar");
      load();
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHeader
        title="Gestoría · Inmatic"
        subtitle={`Revisión y envío de gastos/facturas a ${resendStatus?.inmatic_to || "Inmatic"}`}
        action={
          <div className="inline-flex bg-zinc-900 border border-zinc-800 rounded-lg p-0.5">
            <button onClick={() => setViewMode("galeria")}
                    data-testid="inmatic-view-galeria"
                    className={`px-2.5 py-1.5 rounded text-xs flex items-center gap-1 transition ${viewMode === "galeria" ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-100"}`}>
              <LayoutGrid className="w-3.5 h-3.5" /> Galería
            </button>
            <button onClick={() => setViewMode("tabla")}
                    data-testid="inmatic-view-tabla"
                    className={`px-2.5 py-1.5 rounded text-xs flex items-center gap-1 transition ${viewMode === "tabla" ? "bg-amber-500 text-zinc-900 font-semibold" : "text-zinc-400 hover:text-zinc-100"}`}>
              <ListIcon className="w-3.5 h-3.5" /> Tabla
            </button>
          </div>
        }
      />

      {!resendStatus?.configured && (
        <div className="bg-amber-500/10 border border-amber-700/40 rounded-lg p-3 text-sm flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-300">Resend pendiente de configuración</div>
            <div className="text-xs text-zinc-400">
              Puedes revisar y marcar documentos, pero el envío a Inmatic está deshabilitado hasta que el admin configure
              `RESEND_API_KEY` en el backend.
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2 overflow-x-auto">
        {TABS.map((t) => (
          <button key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`inmatic-tab-${t.k}`}
            className={`px-3 py-1.5 text-sm rounded-t transition-colors whitespace-nowrap ${
              tab === t.k ? `bg-zinc-900 border-b-2 ${t.color} font-semibold`
                          : "text-zinc-500 hover:text-zinc-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Action bar */}
      {tab !== "enviada" && items.length > 0 && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-zinc-300 mr-3 cursor-pointer">
            <input type="checkbox"
              checked={selectedItems.length === items.length && items.length > 0}
              onChange={toggleAll}
              className="w-4 h-4 accent-amber-500" />
            Seleccionar todos ({selectedItems.length} / {items.length})
          </label>
          {tab === "pendiente_revision" && (
            <>
              <Button size="sm" onClick={() => marcar("enviable")} disabled={!selectedItems.length}
                className="bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="mark-enviable-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Marcar como enviable
              </Button>
              <Button size="sm" onClick={marcarNoValida} disabled={!selectedItems.length}
                variant="outline" className="bg-transparent border-zinc-700 text-zinc-300">
                <XCircle className="w-4 h-4 mr-1" /> No válida
              </Button>
            </>
          )}
          {tab === "enviable" && (
            <>
              <Button size="sm" onClick={enviarInmatic}
                disabled={!selectedItems.length || sending || !resendStatus?.configured}
                className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold"
                data-testid="send-inmatic-btn">
                {sending ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Enviando…</>
                          : <><Send className="w-4 h-4 mr-1" /> Enviar {selectedItems.length} a Inmatic</>}
              </Button>
              <Button size="sm" onClick={() => marcar("pendiente_revision")} disabled={!selectedItems.length}
                variant="outline" className="bg-transparent border-zinc-700 text-zinc-300">
                ↩ Devolver a revisión
              </Button>
            </>
          )}
          {tab === "no_valida" && (
            <Button size="sm" onClick={() => marcar("pendiente_revision")} disabled={!selectedItems.length}
              variant="outline" className="bg-transparent border-zinc-700 text-zinc-300">
              ↩ Devolver a revisión
            </Button>
          )}
        </div>
      )}

      {/* Listado: galería o tabla */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500">No hay documentos en este estado.</div>
        ) : viewMode === "galeria" ? (
          <InmaticGaleria items={items} tab={tab} selection={selection} toggle={toggle}
                          keyOf={keyOf} onMarcar={marcarIndividual}
                          onPedirMotivo={(it) => { setMotivoModal({ singleItem: it }); setMotivoText(""); }} />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-900 text-zinc-400 text-xs">
              <tr>
                {tab !== "enviada" && <th className="p-2 w-10"></th>}
                <th className="text-left p-2 w-20">Tipo</th>
                <th className="text-left p-2 w-28">Fecha</th>
                <th className="text-left p-2">Concepto / Emisor</th>
                <th className="text-left p-2">Quien</th>
                <th className="text-right p-2 w-28">Importe</th>
                <th className="text-left p-2 w-20">Doc</th>
                {tab === "no_valida" && <th className="text-left p-2 w-48">Motivo</th>}
                {tab === "enviada" && <th className="text-left p-2 w-40">Enviado</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={keyOf(it)} className="border-t border-zinc-800/60 hover:bg-zinc-900/30">
                  {tab !== "enviada" && (
                    <td className="p-2">
                      <input type="checkbox" checked={!!selection[keyOf(it)]}
                        onChange={() => toggle(it)} className="w-4 h-4 accent-amber-500"
                        data-testid={`inmatic-check-${it._tipo}-${it.id}`} />
                    </td>
                  )}
                  <td className="p-2">
                    <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${
                      it._tipo === "gasto" ? "bg-blue-500/20 text-blue-300"
                                            : "bg-amber-500/20 text-amber-300"}`}>
                      {it._tipo}
                    </span>
                  </td>
                  <td className="p-2 text-zinc-400 font-mono text-xs">{it.fecha}</td>
                  <td className="p-2">
                    <div className="text-zinc-100">{it.concepto || it.emisor_nombre || "—"}</div>
                    {it.numero_factura && <div className="text-xs text-zinc-500">Nº {it.numero_factura}</div>}
                    {it.categoria && <div className="text-xs text-zinc-500">{it.categoria}</div>}
                  </td>
                  <td className="p-2 text-zinc-400 text-xs">{it.operario_nombre || it.emisor_nombre || "—"}</td>
                  <td className="p-2 text-right font-mono font-semibold text-zinc-100">
                    {(it.importe || 0).toFixed(2)} €
                  </td>
                  <td className="p-2">
                    {it.url_adjunto ? (
                      <a href={it.url_adjunto} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs">
                        <Eye className="w-3 h-3" /> Ver
                      </a>
                    ) : (
                      <span className="text-red-400 text-xs">⚠ Sin doc</span>
                    )}
                  </td>
                  {tab === "no_valida" && (
                    <td className="p-2 text-xs text-zinc-400 italic">{it.estado_inmatic_motivo || "—"}</td>
                  )}
                  {tab === "enviada" && (
                    <td className="p-2 text-xs text-zinc-400 font-mono">
                      {(it.inmatic_enviado_at || "").slice(0, 16).replace("T", " ")}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal motivo "no válida" */}
      {motivoModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-950 border border-zinc-800 rounded-xl p-5 max-w-md w-full">
            <h3 className="text-lg font-bold mb-2">Motivo de "no válida"</h3>
            <p className="text-sm text-zinc-400 mb-3">
              {motivoModal?.singleItem
                ? `Indica por qué no se va a enviar a la gestoría: ${motivoModal.singleItem.concepto || motivoModal.singleItem.emisor_nombre || ""}.`
                : `Indica por qué no se va a enviar a la gestoría (${selectedItems.length} documento(s)).`}
            </p>
            <Input value={motivoText} onChange={(e) => setMotivoText(e.target.value)}
              placeholder="Ej: ilegible, sin importe, duplicado…"
              className="bg-zinc-900 border-zinc-700 mb-4" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setMotivoModal(null)}
                className="bg-transparent border-zinc-700">Cancelar</Button>
              <Button onClick={confirmarNoValida} className="bg-zinc-700 hover:bg-zinc-600 text-white">
                Marcar como no válida
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ GALERÍA ============
function InmaticGaleria({ items, tab, selection, toggle, keyOf, onMarcar, onPedirMotivo }) {
  const isReadOnly = tab === "enviada";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 p-3">
      {items.map((it) => (
        <DocCard key={keyOf(it)} it={it} isReadOnly={isReadOnly} tab={tab}
                 selected={!!selection[keyOf(it)]}
                 onToggle={() => toggle(it)}
                 onMarcar={onMarcar}
                 onPedirMotivo={onPedirMotivo} />
      ))}
    </div>
  );
}

function DocCard({ it, isReadOnly, tab, selected, onToggle, onMarcar, onPedirMotivo }) {
  const isImage = it.url_adjunto && /\.(jpg|jpeg|png|webp|heic)(\?|$)/i.test(it.url_adjunto);
  const isPdf = it.url_adjunto && /\.pdf(\?|$)/i.test(it.url_adjunto);
  const tipoColor = it._tipo === "gasto" ? "bg-blue-500/20 text-blue-300 border-blue-700/40"
                                          : "bg-amber-500/20 text-amber-300 border-amber-700/40";
  return (
    <div className={`bg-zinc-900/40 border rounded-lg overflow-hidden flex flex-col transition ${
      selected ? "border-amber-500 ring-1 ring-amber-500/30" : "border-zinc-800 hover:border-zinc-700"
    }`} data-testid={`inmatic-card-${it._tipo}-${it.id}`}>
      {/* Preview del documento */}
      <div className="relative aspect-[3/4] bg-zinc-950 group">
        {it.url_adjunto ? (
          isImage ? (
            <a href={it.url_adjunto} target="_blank" rel="noreferrer" className="block w-full h-full">
              <img src={it.url_adjunto} alt={it.concepto || "doc"}
                   loading="lazy"
                   className="w-full h-full object-contain" />
            </a>
          ) : isPdf ? (
            <a href={it.url_adjunto} target="_blank" rel="noreferrer"
               className="w-full h-full flex flex-col items-center justify-center gap-2 hover:bg-zinc-900 transition">
              <FileText className="w-12 h-12 text-rose-400" />
              <span className="text-[10px] uppercase tracking-wider text-zinc-400 font-mono">PDF</span>
              <span className="text-xs text-amber-400 underline">Abrir</span>
            </a>
          ) : (
            <a href={it.url_adjunto} target="_blank" rel="noreferrer"
               className="w-full h-full flex flex-col items-center justify-center gap-2 hover:bg-zinc-900 transition">
              <ExternalLink className="w-10 h-10 text-zinc-500" />
              <span className="text-xs text-amber-400 underline">Abrir adjunto</span>
            </a>
          )
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-red-400">
            <AlertCircle className="w-10 h-10" />
            <span className="text-xs">Sin documento</span>
          </div>
        )}
        {/* Checkbox y tipo */}
        {!isReadOnly && (
          <label className="absolute top-2 left-2 bg-zinc-900/80 backdrop-blur px-1.5 py-1 rounded cursor-pointer">
            <input type="checkbox" checked={selected} onChange={onToggle}
                   className="w-4 h-4 accent-amber-500 align-middle"
                   data-testid={`inmatic-card-check-${it._tipo}-${it.id}`} />
          </label>
        )}
        <span className={`absolute top-2 right-2 text-[10px] uppercase font-mono px-1.5 py-0.5 rounded border ${tipoColor}`}>
          {it._tipo}
        </span>
      </div>

      {/* Datos */}
      <div className="p-3 flex-1 flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-zinc-400 font-mono text-[10px]">{it.fecha || "—"}</span>
          <span className="font-mono font-bold text-zinc-100">{(it.importe || 0).toFixed(2)} €</span>
        </div>
        <div className="text-sm text-zinc-100 line-clamp-2 min-h-[2.5em]">
          {it.concepto || it.emisor_nombre || "—"}
        </div>
        <div className="text-[11px] text-zinc-500 line-clamp-1">
          {it.operario_nombre || it.emisor_nombre || it.categoria || ""}
          {it.numero_factura && <span> · Nº {it.numero_factura}</span>}
        </div>
        {tab === "no_valida" && it.estado_inmatic_motivo && (
          <div className="text-[11px] text-zinc-500 italic line-clamp-2 border-l-2 border-zinc-700 pl-2 mt-1">
            {it.estado_inmatic_motivo}
          </div>
        )}
        {tab === "enviada" && it.inmatic_enviado_at && (
          <div className="text-[10px] text-amber-400 font-mono mt-auto pt-1">
            ✓ {it.inmatic_enviado_at.slice(0, 16).replace("T", " ")}
          </div>
        )}
      </div>

      {/* Quick actions */}
      {!isReadOnly && (
        <div className="grid grid-cols-2 border-t border-zinc-800">
          {tab === "pendiente_revision" && (
            <>
              <button onClick={() => onMarcar(it, "enviable")}
                      data-testid={`inmatic-card-ok-${it._tipo}-${it.id}`}
                      className="py-2 text-xs text-emerald-300 hover:bg-emerald-500/10 flex items-center justify-center gap-1 border-r border-zinc-800">
                <CheckCircle2 className="w-3.5 h-3.5" /> Enviable
              </button>
              <button onClick={() => onPedirMotivo(it)}
                      data-testid={`inmatic-card-no-${it._tipo}-${it.id}`}
                      className="py-2 text-xs text-zinc-400 hover:bg-zinc-800 flex items-center justify-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> No válida
              </button>
            </>
          )}
          {tab === "enviable" && (
            <>
              <button onClick={() => onMarcar(it, "pendiente_revision")}
                      className="py-2 text-xs text-zinc-400 hover:bg-zinc-800 flex items-center justify-center gap-1 border-r border-zinc-800">
                ↩ A revisión
              </button>
              <button onClick={() => onPedirMotivo(it)}
                      className="py-2 text-xs text-zinc-400 hover:bg-zinc-800 flex items-center justify-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> No válida
              </button>
            </>
          )}
          {tab === "no_valida" && (
            <button onClick={() => onMarcar(it, "pendiente_revision")}
                    className="col-span-2 py-2 text-xs text-zinc-400 hover:bg-zinc-800 flex items-center justify-center gap-1">
              ↩ Devolver a revisión
            </button>
          )}
        </div>
      )}
    </div>
  );
}
